/**
 * Webhook CRUD (FS-230) — operator settings page.
 *
 * Settings → Webhooks. Operators register destination URLs that Trackerity
 * forwards event payloads to. This page handles storage + CRUD UI only; the
 * delivery/retry worker is a separate feature and not invoked here.
 *
 * Layout
 * ------
 *   - Header.
 *   - "New webhook" button → opens drawer.
 *   - Webhooks table with:
 *       * Name, URL preview, event-type chips, Status chip (emerald/zinc),
 *         Test button, Edit button, Delete button.
 *   - Drawer form (create + edit) with:
 *       * Name input with slug-format hint.
 *       * HTTPS URL input with inline validator.
 *       * Event types chip multi-select.
 *       * Headers JSON editor (textarea) with reserved-header warning.
 *       * Description textarea.
 *       * Active toggle.
 *       * Save / Cancel.
 *   - Test button fires POST /:id/test → "Test payload queued (202)" toast.
 *
 * Brand: emerald → sky gradient header, Inter, zinc-bordered cards,
 * 6-8px rounding, light theme.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	ALLOWED_EVENT_TYPES,
	EMPTY_WEBHOOK_DRAFT,
	createWebhookRemote,
	deleteWebhookRemote,
	formatTimestamp,
	listWebhooks,
	statusBadgeClass,
	statusLabel,
	testWebhookRemote,
	toggleWebhookActive,
	updateWebhookRemote,
	urlHint,
	webhookCrudQueryKeys,
	webhookDraftSchema,
	webhookToDraft,
	type WebhookDraft,
	type WebhookEventType,
	type WebhookView,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — shared vocabulary across the page.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const TEXTAREA = `${INPUT} font-mono text-xs`;
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const CODE =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-xs text-zinc-800";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface WebhookCrudPageProps {
	/** Base URL label shown in header. Decorative only. */
	subtitle?: string;
}

export function WebhookCrudPage(_props: WebhookCrudPageProps = {}) {
	const queryClient = useQueryClient();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editing, setEditing] = useState<WebhookView | null>(null);
	const [draft, setDraft] = useState<WebhookDraft>(EMPTY_WEBHOOK_DRAFT);
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});

	const listQuery = useQuery({
		queryKey: webhookCrudQueryKeys.list(),
		queryFn: listWebhooks,
	});

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: webhookCrudQueryKeys.list() });

	const createMutation = useMutation({
		mutationFn: async (d: WebhookDraft) => {
			const parsed = webhookDraftSchema.safeParse(d);
			if (!parsed.success) throw new DraftError(parsed.errors);
			return createWebhookRemote(parsed.data);
		},
		onSuccess: (row) => {
			toast.success(`Webhook "${row.name}" created.`);
			closeDrawer();
			invalidate();
		},
		onError: (err: unknown) => {
			handleDraftOrError(err);
		},
	});

	const updateMutation = useMutation({
		mutationFn: async ({ id, d }: { id: string; d: WebhookDraft }) => {
			const parsed = webhookDraftSchema.safeParse(d);
			if (!parsed.success) throw new DraftError(parsed.errors);
			return updateWebhookRemote(id, parsed.data);
		},
		onSuccess: (row) => {
			toast.success(`Webhook "${row.name}" updated.`);
			closeDrawer();
			invalidate();
		},
		onError: (err: unknown) => {
			handleDraftOrError(err);
		},
	});

	const toggleMutation = useMutation({
		mutationFn: ({ id, active }: { id: string; active: boolean }) =>
			toggleWebhookActive(id, active),
		onSuccess: (row) => {
			toast.success(
				row.active
					? `Webhook "${row.name}" resumed.`
					: `Webhook "${row.name}" paused.`,
			);
			invalidate();
		},
		onError: (err: Error) => toast.error(err.message || "Toggle failed."),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteWebhookRemote(id),
		onSuccess: () => {
			toast.success("Webhook deleted.");
			invalidate();
		},
		onError: (err: Error) => toast.error(err.message || "Delete failed."),
	});

	const testMutation = useMutation({
		mutationFn: (id: string) => testWebhookRemote(id),
		onSuccess: (res) => {
			toast.success(`Test payload queued (202). test_id=${res.test_id}`);
		},
		onError: (err: Error) => toast.error(err.message || "Test failed."),
	});

	function handleDraftOrError(err: unknown) {
		if (err instanceof DraftError) {
			setDraftErrors(err.errors);
			return;
		}
		const message = err instanceof Error ? err.message : "Unknown error";
		toast.error(message);
	}

	function openNew() {
		setEditing(null);
		setDraft(EMPTY_WEBHOOK_DRAFT);
		setDraftErrors({});
		setDrawerOpen(true);
	}

	function openEdit(row: WebhookView) {
		setEditing(row);
		setDraft(webhookToDraft(row));
		setDraftErrors({});
		setDrawerOpen(true);
	}

	function closeDrawer() {
		setDrawerOpen(false);
		setEditing(null);
		setDraft(EMPTY_WEBHOOK_DRAFT);
		setDraftErrors({});
	}

	function submit() {
		if (editing) {
			updateMutation.mutate({ id: editing.id, d: draft });
		} else {
			createMutation.mutate(draft);
		}
	}

	const saving = createMutation.isPending || updateMutation.isPending;
	const rows = listQuery.data ?? [];

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 pb-24 pt-8">
			<header className="flex items-start justify-between gap-4">
				<div className="flex flex-col gap-2">
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Webhooks
					</h1>
					<p className="text-sm text-zinc-600">
						Forward Trackerity events to your own HTTPS endpoint. Subscribe to
						specific event types, add custom headers, and test-fire a synthetic
						payload before going live.
					</p>
				</div>
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={openNew}
				>
					New webhook
				</button>
			</header>

			<section className={`${CARD} p-5`} aria-labelledby="list-hdr">
				<div className="flex items-center justify-between">
					<h2 id="list-hdr" className="text-base font-semibold text-zinc-900">
						Registered webhooks
					</h2>
					<span className={HINT}>{rows.length} total</span>
				</div>
				<div className="mt-3 overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-3 py-2">Name</th>
								<th className="px-3 py-2">URL</th>
								<th className="px-3 py-2">Events</th>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2">Updated</th>
								<th className="px-3 py-2 text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{rows.map((row) => (
								<WebhookRow
									key={row.id}
									row={row}
									onEdit={() => openEdit(row)}
									onToggle={() =>
										toggleMutation.mutate({ id: row.id, active: !row.active })
									}
									onDelete={() => {
										if (
											typeof window !== "undefined" &&
											window.confirm(`Delete webhook "${row.name}"?`)
										) {
											deleteMutation.mutate(row.id);
										}
									}}
									onTest={() => testMutation.mutate(row.id)}
									busy={
										toggleMutation.isPending ||
										deleteMutation.isPending ||
										testMutation.isPending
									}
								/>
							))}
							{!listQuery.isLoading && rows.length === 0 && (
								<tr>
									<td colSpan={6} className="px-3 py-6 text-center text-zinc-500">
										No webhooks yet. Create one to start forwarding events.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>

			{drawerOpen && (
				<WebhookDrawer
					editing={editing}
					draft={draft}
					errors={draftErrors}
					saving={saving}
					onChange={(next) => setDraft({ ...draft, ...next })}
					onToggleEvent={(ev) =>
						setDraft({
							...draft,
							event_types: draft.event_types.includes(ev)
								? draft.event_types.filter((e) => e !== ev)
								: [...draft.event_types, ev],
						})
					}
					onClose={closeDrawer}
					onSubmit={submit}
				/>
			)}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function WebhookRow({
	row,
	onEdit,
	onToggle,
	onDelete,
	onTest,
	busy,
}: {
	row: WebhookView;
	onEdit: () => void;
	onToggle: () => void;
	onDelete: () => void;
	onTest: () => void;
	busy: boolean;
}) {
	return (
		<tr className="align-top">
			<td className="px-3 py-2 text-zinc-900">
				<div className="font-medium">{row.name}</div>
				{row.description && (
					<div className="mt-0.5 text-xs text-zinc-500">{row.description}</div>
				)}
			</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs break-all">{row.url}</code>
			</td>
			<td className="px-3 py-2">
				<div className="flex flex-wrap gap-1">
					{row.event_types.map((ev) => (
						<span
							key={ev}
							className="inline-flex rounded-[6px] border border-sky-200 bg-sky-50 px-2 py-0.5 font-mono text-xs text-sky-700"
						>
							{ev}
						</span>
					))}
				</div>
			</td>
			<td className="px-3 py-2">
				<span
					className={`inline-flex rounded-[6px] border px-2 py-0.5 text-xs ${statusBadgeClass(row.active)}`}
				>
					{statusLabel(row.active)}
				</span>
			</td>
			<td className="px-3 py-2 text-xs text-zinc-500">
				{formatTimestamp(row.updated_at)}
			</td>
			<td className="px-3 py-2">
				<div className="flex justify-end gap-2">
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onTest}
						disabled={busy}
					>
						Test
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onToggle}
						disabled={busy}
					>
						{row.active ? "Pause" : "Resume"}
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onEdit}
						disabled={busy}
					>
						Edit
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={onDelete}
						disabled={busy}
					>
						Delete
					</button>
				</div>
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Drawer (create + edit)
// ---------------------------------------------------------------------------

function WebhookDrawer({
	editing,
	draft,
	errors,
	saving,
	onChange,
	onToggleEvent,
	onClose,
	onSubmit,
}: {
	editing: WebhookView | null;
	draft: WebhookDraft;
	errors: Record<string, string>;
	saving: boolean;
	onChange: (next: Partial<WebhookDraft>) => void;
	onToggleEvent: (ev: WebhookEventType) => void;
	onClose: () => void;
	onSubmit: () => void;
}) {
	const hint = useMemo(() => urlHint(draft.url), [draft.url]);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="drawer-hdr"
			className="fixed inset-0 z-50 flex justify-end bg-zinc-900/40"
		>
			<div
				className={`flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-zinc-200 bg-white`}
			>
				<header className="flex items-start justify-between border-b border-zinc-200 px-6 py-4">
					<div>
						<h2 id="drawer-hdr" className="text-lg font-semibold text-zinc-900">
							{editing ? `Edit "${editing.name}"` : "New webhook"}
						</h2>
						<p className={HINT}>
							Destination must accept POST JSON over HTTPS.
						</p>
					</div>
					<button type="button" className={BTN_SECONDARY} onClick={onClose}>
						Close
					</button>
				</header>

				<div className="flex flex-1 flex-col gap-5 px-6 py-5">
					{/* Name */}
					<div className="flex flex-col gap-1">
						<label htmlFor="wh-name" className={LABEL}>
							Name
						</label>
						<input
							id="wh-name"
							className={INPUT}
							value={draft.name}
							onChange={(e) => onChange({ name: e.target.value })}
							placeholder="partner_acme"
						/>
						<p className={HINT}>
							Slug form: a-z, 0-9, underscore, hyphen. 2 to 64 chars.
						</p>
						{errors.name && <p className={ERROR_TEXT}>{errors.name}</p>}
					</div>

					{/* URL */}
					<div className="flex flex-col gap-1">
						<label htmlFor="wh-url" className={LABEL}>
							URL
						</label>
						<input
							id="wh-url"
							className={INPUT}
							value={draft.url}
							onChange={(e) => onChange({ url: e.target.value })}
							placeholder="https://api.yourapp.com/hooks/trackerity"
							type="url"
						/>
						<p
							className={
								hint.ok
									? "text-xs text-emerald-700"
									: draft.url.length === 0
										? HINT
										: ERROR_TEXT
							}
						>
							{hint.message}
						</p>
						{errors.url && <p className={ERROR_TEXT}>{errors.url}</p>}
					</div>

					{/* Event types */}
					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between">
							<label className={LABEL}>Event types</label>
							<span className={HINT}>
								{draft.event_types.length} selected
							</span>
						</div>
						<div className="flex flex-wrap gap-2">
							{ALLOWED_EVENT_TYPES.map((ev) => {
								const on = draft.event_types.includes(ev);
								return (
									<button
										key={ev}
										type="button"
										onClick={() => onToggleEvent(ev)}
										className={`rounded-[6px] border px-2 py-1 font-mono text-xs transition-colors ${
											on
												? "border-emerald-300 bg-emerald-50 text-emerald-800"
												: "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
										}`}
									>
										{ev}
									</button>
								);
							})}
						</div>
						<p className={HINT}>
							"*" subscribes to every current and future event type.
						</p>
						{errors.event_types && (
							<p className={ERROR_TEXT}>{errors.event_types}</p>
						)}
					</div>

					{/* Headers */}
					<div className="flex flex-col gap-1">
						<label htmlFor="wh-hdrs" className={LABEL}>
							Extra headers (optional JSON)
						</label>
						<textarea
							id="wh-hdrs"
							className={TEXTAREA}
							rows={6}
							value={draft.headers_text}
							onChange={(e) => onChange({ headers_text: e.target.value })}
							placeholder={`{\n  "X-Source": "trackerity"\n}`}
						/>
						<p className={HINT}>
							Reserved: Authorization, Content-Type, Content-Length, Host.
						</p>
						{errors.headers_text && (
							<p className={ERROR_TEXT}>{errors.headers_text}</p>
						)}
					</div>

					{/* Description */}
					<div className="flex flex-col gap-1">
						<label htmlFor="wh-desc" className={LABEL}>
							Description (optional)
						</label>
						<textarea
							id="wh-desc"
							className={INPUT}
							rows={3}
							value={draft.description}
							onChange={(e) => onChange({ description: e.target.value })}
							placeholder="Forwarding conversions to our CRM pipeline."
						/>
						{errors.description && (
							<p className={ERROR_TEXT}>{errors.description}</p>
						)}
					</div>

					{/* Active */}
					<div className="flex items-center justify-between rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2">
						<div>
							<div className={LABEL}>Active</div>
							<div className={HINT}>
								Inactive webhooks are skipped by the delivery worker.
							</div>
						</div>
						<label className="inline-flex items-center gap-2 text-sm text-zinc-700">
							<input
								type="checkbox"
								className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
								checked={draft.active}
								onChange={(e) => onChange({ active: e.target.checked })}
							/>
							<span>{draft.active ? "On" : "Off"}</span>
						</label>
					</div>

					{editing && (
						<div className="text-xs text-zinc-500">
							Created {formatTimestamp(editing.created_at)}
							{" \u2022 "}Updated {formatTimestamp(editing.updated_at)}
							{" \u2022 "}
							<code className={CODE}>{editing.id}</code>
						</div>
					)}
				</div>

				<footer className="flex justify-end gap-2 border-t border-zinc-200 px-6 py-4">
					<button type="button" className={BTN_SECONDARY} onClick={onClose}>
						Cancel
					</button>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={onSubmit}
						disabled={saving}
					>
						{saving ? "Saving\u2026" : editing ? "Save changes" : "Create webhook"}
					</button>
				</footer>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

class DraftError extends Error {
	readonly errors: Record<string, string>;
	constructor(errors: Record<string, string>) {
		super("Validation failed");
		this.name = "DraftError";
		this.errors = errors;
	}
}

export default WebhookCrudPage;
